import { useState } from "react";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { MoneyText } from "@workspace/accounting/components";
import { CheckOperation } from "@workspace/operations/check-operation";
import type { Operation } from "@workspace/operations/types";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import { Spinner } from "@workspace/ui/components/spinner";
import { Textarea } from "@workspace/ui/components/textarea";

import {
	MIN_EVIDENCE,
	REASON_COPY,
	refundsAttentionApi,
	refusalCode,
	resolveRefusal,
	STALE_CODES,
	type RefundNeedingAttention,
	type RefundResolution,
} from "../refunds-attention-api";

const RESOLVE = "staff-terminal-refund-resolve";

/** HA-984 — an order's refund, or a Pay Tab refund (one Stripe refund over every order it was spread over). */
const whereOf = (refund: RefundNeedingAttention) =>
	refund.tabId
		? "Pay Tab refund, over every order it was spread over"
		: `on order ${refund.orderId}`;

export interface RefundsNeedingAttentionProps {
	tenant: string;
	venueId: string;
	operations: Operation[];
}

/**
 * HA-836 — card refunds HA-835's reconciler couldn't settle and flagged for a person: stuck for
 * a day, on an account it can't tell, or missing at Stripe. Oldest first, as the server sends
 * them; a refund leaves the list when its status changes, so a refetch after a resolution is all
 * it takes. Resolving needs epos:refund:resolve (the venue Owner's by default). Nothing shows
 * while none need a person.
 */
export function RefundsNeedingAttention({
	tenant,
	venueId,
	operations,
}: RefundsNeedingAttentionProps) {
	const list = useQuery(refundsAttentionApi.listOptions(tenant, venueId));
	const [resolving, setResolving] = useState<RefundNeedingAttention | null>(
		null,
	);
	const refunds = list.data?.refunds ?? [];

	if (refunds.length === 0) return null;

	return (
		<section
			aria-labelledby="refunds-needing-attention"
			className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30"
		>
			<h3
				id="refunds-needing-attention"
				className="flex items-center gap-2 font-semibold"
			>
				<AlertTriangle className="size-4 text-amber-600" />
				Refunds needing attention ({refunds.length})
			</h3>
			<ul className="space-y-2">
				{refunds.map((refund) => (
					<li
						key={refund.refundId}
						data-refund={refund.refundId}
						className="flex items-start justify-between gap-4 rounded-md bg-background p-3"
					>
						<div className="space-y-1">
							<div className="font-medium">
								<MoneyText {...refund.total} /> {whereOf(refund)}
							</div>
							<div className="text-sm">{REASON_COPY[refund.reason]}</div>
							<div className="text-xs text-muted-foreground">
								{refund.evidence}
							</div>
						</div>
						<CheckOperation operation={RESOLVE} operations={operations}>
							<Button
								size="sm"
								variant="outline"
								onClick={() => setResolving(refund)}
								aria-label={
									refund.tabId
										? "Resolve the Pay Tab refund"
										: `Resolve the refund on order ${refund.orderId}`
								}
							>
								<ShieldCheck className="size-4" />
								Resolve
							</Button>
						</CheckOperation>
					</li>
				))}
			</ul>

			{resolving && (
				<ResolveRefundDialog
					refund={resolving}
					tenant={tenant}
					venueId={venueId}
					onClose={() => setResolving(null)}
				/>
			)}
		</section>
	);
}

function ResolveRefundDialog({
	refund,
	tenant,
	venueId,
	onClose,
}: {
	refund: RefundNeedingAttention;
	tenant: string;
	venueId: string;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const [outcome, setOutcome] = useState<RefundResolution | null>(null);
	const [evidence, setEvidence] = useState("");
	const [stripeRefundId, setStripeRefundId] = useState(
		refund.stripeRefundId ?? "",
	);

	const refresh = () =>
		Promise.all([
			queryClient.invalidateQueries({
				queryKey: refundsAttentionApi.listOptions(tenant, venueId).queryKey,
			}),
			queryClient.invalidateQueries({ queryKey: [tenant, venueId, "epos"] }),
		]);

	const resolve = useMutation({
		mutationFn: () =>
			refundsAttentionApi.resolve(tenant, venueId, refund, {
				outcome: outcome!,
				evidence: evidence.trim(),
				...(outcome === "succeeded" && stripeRefundId.trim()
					? { stripeRefundId: stripeRefundId.trim() }
					: {}),
			}),
		onSuccess: async (result) => {
			toast.success(
				result.status === "Failed"
					? "Refund marked failed. It can be retried."
					: "Refund marked succeeded",
			);
			onClose();
			await refresh();
		},
		onError: async (error) => {
			if (STALE_CODES.has(refusalCode(error) ?? "")) await refresh();
		},
	});

	const enoughEvidence = evidence.trim().length >= MIN_EVIDENCE;

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open && !resolve.isPending) onClose();
			}}
		>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>Resolve this refund</DialogTitle>
					<DialogDescription>
						<MoneyText {...refund.total} /> {whereOf(refund)}.{" "}
						{REASON_COPY[refund.reason]}.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="flex gap-2" role="group" aria-label="Outcome">
						<Button
							type="button"
							variant={outcome === "succeeded" ? "default" : "outline"}
							aria-pressed={outcome === "succeeded"}
							onClick={() => setOutcome("succeeded")}
						>
							Mark succeeded
						</Button>
						<Button
							type="button"
							variant={outcome === "failed" ? "destructive" : "outline"}
							aria-pressed={outcome === "failed"}
							onClick={() => setOutcome("failed")}
						>
							Mark failed
						</Button>
					</div>

					{outcome === "failed" && (
						<p className="text-sm text-destructive">
							Only mark failed if the customer did NOT get the money back. A
							failed refund can be retried, which refunds them again.
						</p>
					)}

					<div className="space-y-1">
						<Label htmlFor="resolve-evidence">Evidence</Label>
						<Textarea
							id="resolve-evidence"
							value={evidence}
							onChange={(e) => setEvidence(e.target.value)}
							placeholder="What shows it: a Stripe dashboard entry, a bank transfer reference…"
						/>
						<p className="text-xs text-muted-foreground">
							At least {MIN_EVIDENCE} characters. It's kept with the refund.
						</p>
					</div>

					{outcome === "succeeded" && (
						<div className="space-y-1">
							<Label htmlFor="resolve-stripe-refund-id">Stripe refund id</Label>
							<Input
								id="resolve-stripe-refund-id"
								value={stripeRefundId}
								onChange={(e) => setStripeRefundId(e.target.value)}
								placeholder="re_… (leave empty for a refund made outside Stripe)"
							/>
						</div>
					)}

					{resolve.isError && (
						<p role="alert" className="text-sm text-destructive">
							{resolveRefusal(resolve.error)}
						</p>
					)}
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						onClick={onClose}
						disabled={resolve.isPending}
					>
						Cancel
					</Button>
					<Button
						type="button"
						onClick={() => resolve.mutate()}
						disabled={!outcome || !enoughEvidence || resolve.isPending}
					>
						{resolve.isPending && <Spinner />}
						Resolve refund
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
